import {
  BookOpen01,
  MessageHeartCircle,
  MusicNote01,
  Pin02,
  Plus,
  Share04,
  Trash01,
  VideoRecorder,
} from "@untitledui/icons"
import { useState } from "react"

import { Avatar } from "@/components/ui/avatar"
import { BackgroundPattern } from "@/components/ui/background-pattern"
import { Badge, BadgeWithDot } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ButtonGroup, ButtonGroupItem } from "@/components/ui/button-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Dropdown } from "@/components/ui/dropdown-menu"
import { EmptyState } from "@/components/ui/empty-state"
import { Input, InputBase, InputGroup } from "@/components/ui/input"
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from "@/components/ui/modal"
import { PaginationPageMinimalCenter } from "@/components/ui/pagination"
import { RadioButton, RadioGroup } from "@/components/ui/radio-buttons"
import {
  RadioGroupAvatar,
  RadioGroupIconSimple,
} from "@/components/ui/radio-groups"
import { SlideoutMenu } from "@/components/ui/slideout-menu"
import { Table } from "@/components/ui/table"
import { TabList, Tabs } from "@/components/ui/tabs"
import { TextArea } from "@/components/ui/textarea"
import { Toggle } from "@/components/ui/toggle"
import { Tooltip } from "@/components/ui/tooltip"

import { ALL_CONTRIBUTORS, SUGGESTION_ROWS, WHY_NOTES } from "../_fixtures"

import { ManualSection, Plate } from "./primitives"

const FORMAT_BADGE_COLOR = {
  Music: "brand",
  Video: "blue-light",
  Article: "gray",
} as const

// Soft limit for the "why" note — long enough for a real story, short
// enough to keep the surface near-zero-friction. Drives the live counter.
const WHY_LIMIT = 280

// Format as icon-led options (RadioGroupIconSimple), not a Select: the
// shared-desk feel, and a kit component the manual otherwise never shows.
const FORMAT_OPTIONS = [
  {
    value: "music",
    title: "Music",
    secondaryTitle: "· song or playlist",
    description: "A track you reached for.",
    icon: MusicNote01,
  },
  {
    value: "video",
    title: "Video",
    secondaryTitle: "· clip or talk",
    description: "Something you watched and kept.",
    icon: VideoRecorder,
  },
  {
    value: "article",
    title: "Article",
    secondaryTitle: "· essay or writing",
    description: "Words that said it better than you could.",
    icon: BookOpen01,
  },
]

// "Post as" — the byline moment. Value before identity: a guest contributes
// with just a name; a member's history links up. (RadioGroupAvatar renders
// the kit Avatar via avatarUrl, not the app's beam Avatar — acceptable for
// the manual; the beam variant is showcased in the Avatars plate.)
const POST_AS_OPTIONS = [
  {
    id: "guest",
    name: "Theo Marsh",
    username: "· as a guest",
    title: "No account needed — your name still travels with your note.",
    avatarUrl: "",
  },
  {
    id: "member",
    name: "Mara Quinn",
    username: "· signed in",
    title: "Your suggestion history links to your profile.",
    avatarUrl: "",
  },
]

// The kit, Batyr-toned. Every plate is stock Untitled UI through the
// ui/ wrapper layer; nothing here is an invented component. Plates are
// individual views: one yellow moment each.
export function InventorySection() {
  const [page, setPage] = useState(1)
  // Drives the "why" character counter without controlling the field
  // (docs/core/patterns/ui/forms.md — "live UI without full control").
  const [why, setWhy] = useState("")

  return (
    <ManualSection
      id="components"
      title="The Kit, Batyr-Toned"
      lede={
        <>
          All 675 vendored Untitled UI PRO components inherit the desk through{" "}
          <code className="font-mono text-sm">theme.css</code> alone. App code
          imports <code className="font-mono text-sm">@/components/ui/*</code>{" "}
          and never the kit directly. These plates show the working vocabulary
          with its states: default, hover, focus, disabled, error.
        </>
      }
    >
      <h3 className="text-primary text-xl font-semibold">Actions</h3>
      <div className="grid gap-6 lg:grid-cols-2">
        <Plate
          title="Button hierarchy"
          note="One primary action per view. Secondary and tertiary carry everything else; links go Byline Blue."
          importPath="@/components/ui/button"
        >
          <div className="flex flex-wrap items-center gap-3">
            <Button color="primary" size="md" iconLeading={Plus}>
              Suggest a track
            </Button>
            <Button color="secondary" size="md" iconLeading={Share04}>
              Share
            </Button>
            <Button color="tertiary" size="md">
              Save for later
            </Button>
            <Button color="link-color" size="md">
              View the brief
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button color="primary" size="md" isDisabled>
              Suggest a track
            </Button>
            <Button color="secondary" size="md" isLoading showTextWhileLoading>
              Sharing…
            </Button>
            <Button color="primary-destructive" size="md" iconLeading={Trash01}>
              Retire suggestion
            </Button>
          </div>
        </Plate>

        <Plate
          title="Button group"
          note="Segmented filters for curation states, never engagement scores."
          importPath="@/components/ui/button-group"
        >
          <ButtonGroup defaultSelectedKeys={["open"]} aria-label="Filter slots">
            <ButtonGroupItem id="all">All slots</ButtonGroupItem>
            <ButtonGroupItem id="open">Open</ButtonGroupItem>
            <ButtonGroupItem id="filled">Filled</ButtonGroupItem>
          </ButtonGroup>
          <p className="text-tertiary text-sm">
            Weight and ink carry the active state. Accent color serves only as
            the state indicator.
          </p>
        </Plate>
      </div>

      <h3 className="text-primary mt-4 text-xl font-semibold">Forms</h3>
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Plate
          title="Suggesting, with near-zero friction"
          note="A whole guest contribution in one frame. The curator's prompt sets it up; the 'why' is the product, so it carries the most weight; identity costs only a name. One yellow moment: the action."
          importPath="@/components/ui/input · textarea · radio-groups · button"
        >
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex flex-col gap-5"
          >
            {/* Editorial frame: the slot is something a peer is asking for,
                not a blank field. */}
            <div className="border-secondary flex items-start gap-3 border-b pb-5">
              <Avatar size="sm" name="Priya Raman" avatarUrl={null} />
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className="text-secondary text-sm">
                  <span className="text-primary font-semibold">Priya</span> is
                  curating this slot
                </p>
                <p className="text-tertiary text-sm">
                  &ldquo;A track for the quiet weeks — the song you reached for
                  when nothing else fit.&rdquo;
                </p>
              </div>
            </div>

            <InputGroup
              size="md"
              name="url"
              label="Where can people find it?"
              prefix="https://"
              hint="Music, video, or writing — anything that helped."
            >
              <InputBase placeholder="open.spotify.com/track/…" />
            </InputGroup>

            {/* The hero field: the why is the product, so it gets the most
                room and the live counter, never a buried 3-row box. */}
            <TextArea
              name="why"
              label="Why did this matter to you?"
              placeholder="One honest line is plenty. What did it do for you on the hard days?"
              rows={5}
              maxLength={WHY_LIMIT}
              onChange={setWhy}
              hint={
                <span className="flex justify-between gap-4">
                  <span>
                    This note travels with the track, under your name.
                  </span>
                  <span className="text-tertiary tabular-nums">
                    {why.length}/{WHY_LIMIT}
                  </span>
                </span>
              }
            />

            <div className="flex flex-col gap-1.5">
              <span className="text-secondary text-sm font-medium">
                What kind of thing is it?
              </span>
              <RadioGroupIconSimple
                aria-label="Format"
                defaultValue="music"
                items={FORMAT_OPTIONS}
              />
            </div>

            {/* Value before identity: no sign-up wall, no password. */}
            <div className="border-secondary flex flex-col gap-4 border-t pt-5">
              <p className="text-tertiary text-sm">
                No account needed. Contributing costs a link, a story, and a
                name.
              </p>
              <div className="flex flex-col gap-x-5 gap-y-5 sm:flex-row">
                <Input
                  name="name"
                  label="Your name"
                  placeholder="First name is plenty"
                  wrapperClassName="flex-1"
                />
                <Input
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="you@example.com"
                  hint="Only if a curator needs to ask you something."
                  wrapperClassName="flex-1"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-secondary text-sm font-medium">
                  Post as
                </span>
                <RadioGroupAvatar
                  aria-label="Post as"
                  defaultValue="guest"
                  items={POST_AS_OPTIONS}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-1">
              <Button color="tertiary" size="md" type="button">
                Save as draft
              </Button>
              <Button
                color="primary"
                size="md"
                type="submit"
                iconLeading={Plus}
              >
                Add to the brief
              </Button>
            </div>
          </form>
        </Plate>

        <Plate
          title="States: error, disabled, choices"
          note="Error stays on UUI's semantic crimson, which is distinct from Red Pencil. Disabled states stay perceivable."
          importPath="@/components/ui/checkbox · radio-buttons · toggle"
        >
          <div className="flex flex-col gap-5">
            <Input
              label="Your email"
              type="email"
              defaultValue="theo@"
              isInvalid
              errorMessage="That email looks incomplete. Check the domain."
            />
            <Input
              label="Display name"
              defaultValue="Theo Marsh"
              isDisabled
              hint="Names are locked while a suggestion is in review."
            />
            <RadioGroup
              aria-label="Who can see your name"
              defaultValue="everyone"
              className="flex flex-col gap-3"
            >
              <RadioButton
                value="everyone"
                label="Show my name on the brief"
                hint="Your byline appears beside your note."
              />
              <RadioButton value="curators" label="Only curators see my name" />
            </RadioGroup>
            <div className="flex flex-col gap-3">
              <Checkbox
                label="Email me when this brief fills"
                hint="One email at most, and no digests or streaks."
                defaultSelected
              />
              <Toggle
                label="Show my history to people I share with"
                size="sm"
              />
            </div>
          </div>
        </Plate>
      </div>

      <h3 className="text-primary mt-4 text-xl font-semibold">
        Identity &amp; attribution
      </h3>
      <div className="grid gap-6 lg:grid-cols-2">
        <Plate
          title="Avatars: many visible hands"
          note="Every person-shaped surface uses the app Avatar: an uploaded photo when present, a deterministic Batyr-palette beam when not. Guests get the same dignity as members."
          importPath="@/components/ui/avatar"
        >
          <div className="flex items-end gap-4">
            {(["xs", "sm", "md", "lg", "xl"] as const).map((size) => (
              <Avatar
                key={size}
                size={size}
                name="Priya Raman"
                avatarUrl={null}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center -space-x-2">
              {ALL_CONTRIBUTORS.slice(0, 4).map((c) => (
                <Avatar
                  key={c.id}
                  size="sm"
                  name={c.display_name}
                  avatarUrl={c.user?.avatar_url}
                  className="ring-2 ring-white"
                />
              ))}
            </div>
            <span className="text-tertiary text-sm">
              Priya, June, Theo + 3 others are building this
            </span>
          </div>
        </Plate>

        <Plate
          title="Badges: curation language"
          note="Counts describe the work in many hands ('3 suggestions'), never engagement scoring. Brand badges carry warm tints with Ink text."
          importPath="@/components/ui/badge"
        >
          <div className="flex flex-wrap items-center gap-2.5">
            <Badge color="brand" size="md">
              3 suggestions
            </Badge>
            <Badge color="gray" size="md">
              Guest contributor
            </Badge>
            <BadgeWithDot color="success" size="md">
              Accepted · let it stand
            </BadgeWithDot>
            <BadgeWithDot color="warning" size="md">
              In review
            </BadgeWithDot>
            <Badge color="blue-light" size="md">
              Curated by June
            </Badge>
          </div>
        </Plate>
      </div>

      <h3 className="text-primary mt-4 text-xl font-semibold">Data</h3>
      <div className="flex flex-col gap-6">
        <Plate
          title="The suggestions table"
          note="Built for scanning speed: row-header track names, text-tertiary support cells, tabular numerals right-aligned. Dense where data demands it, generous everywhere else."
          importPath="@/components/ui/table"
          bodyClassName="gap-0 p-0"
        >
          <div className="px-6 pt-5">
            <Tabs orientation="horizontal" defaultSelectedKey="all">
              <TabList
                size="sm"
                type="underline"
                items={[
                  { id: "all", label: "All suggestions", badge: 23 },
                  { id: "accepted", label: "Accepted", badge: 7 },
                  { id: "review", label: "In review", badge: 4 },
                ]}
              />
            </Tabs>
          </div>
          <div className="overflow-x-auto">
            <Table aria-label="Suggestions" size="sm">
              <Table.Header>
                <Table.Head
                  id="track"
                  isRowHeader
                  label="Track"
                  className="w-full"
                />
                <Table.Head id="contributor" label="Suggested by" />
                <Table.Head id="why" label="Why it matters" />
                <Table.Head id="format" label="Format" />
                <Table.Head
                  id="duration"
                  label="Length"
                  className="text-right"
                />
              </Table.Header>
              <Table.Body items={SUGGESTION_ROWS}>
                {(row) => (
                  <Table.Row id={row.id}>
                    <Table.Cell>
                      <div className="flex min-w-0 flex-col">
                        <span className="text-primary text-sm font-medium whitespace-nowrap">
                          {row.track}
                        </span>
                        <span className="text-tertiary text-sm whitespace-nowrap">
                          {row.artist}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <Avatar size="xs" name={row.contributor} />
                        <span className="text-secondary text-sm whitespace-nowrap">
                          {row.contributor}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-tertiary line-clamp-1 max-w-64 text-sm">
                        {row.note}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={FORMAT_BADGE_COLOR[row.format]} size="sm">
                        {row.format}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      <span className="text-tertiary text-sm tabular-nums">
                        {row.duration}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>
          <div className="px-6 pb-5">
            <PaginationPageMinimalCenter
              page={page}
              total={5}
              onPageChange={setPage}
            />
          </div>
        </Plate>

        <div className="grid items-start gap-6 lg:grid-cols-2">
          <Plate
            title="Empty states teach"
            note="An empty slot is an invitation written by the curator, with one clear next action. It is never a bare 'No results'."
            importPath="@/components/ui/empty-state"
          >
            <div className="relative isolate overflow-hidden rounded-lg">
              {/* Quiet texture, confined to the content area and faded by the
                  pattern's own radial mask — never reaches the figcaption. Warm
                  manila (vs the kit's cool gray-200 default) keeps the navy copy
                  legible on the white card. */}
              <BackgroundPattern
                pattern="grid"
                size="md"
                className="text-batyr-manila absolute top-1/2 left-1/2 -z-10 -translate-x-1/2 -translate-y-1/2"
              />
              <EmptyState size="sm" className="py-6">
                <EmptyState.Header pattern="none">
                  <EmptyState.FeaturedIcon
                    color="gray"
                    theme="modern"
                    icon={MessageHeartCircle}
                  />
                </EmptyState.Header>
                <EmptyState.Content>
                  <p className="text-primary text-md text-center font-semibold">
                    This slot is waiting for your story
                  </p>
                  <EmptyState.Description>
                    Priya is looking for a track for the quiet weeks: the song
                    you reached for when nothing else fit.
                  </EmptyState.Description>
                </EmptyState.Content>
                <EmptyState.Footer>
                  <Button color="primary" size="md" iconLeading={Plus}>
                    Suggest the first track
                  </Button>
                </EmptyState.Footer>
              </EmptyState>
            </div>
          </Plate>

          <Plate
            title="Menus & tooltips"
            note="Quiet affordances: dropdowns float on real shadow steps; tooltips carry the contributor's role and tagline."
            importPath="@/components/ui/dropdown-menu · tooltip"
          >
            <div className="flex flex-wrap items-center gap-4">
              <Dropdown.Root>
                <Button color="secondary" size="md">
                  Track actions
                </Button>
                <Dropdown.Popover>
                  <Dropdown.Menu>
                    <Dropdown.Item icon={Pin02} label="Pin to the desk" />
                    <Dropdown.Item icon={Share04} label="Share this track" />
                    <Dropdown.Separator />
                    <Dropdown.Item icon={Trash01} label="Retire suggestion" />
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.Root>

              <Tooltip
                title="June Okafor"
                description="Clinical advisor. Collects songs her clients bring her."
                arrow
              >
                <Button color="tertiary" size="md">
                  Hover the byline
                </Button>
              </Tooltip>
            </div>
          </Plate>
        </div>
      </div>

      <h3 className="text-primary mt-4 text-xl font-semibold">Overlays</h3>
      <div className="grid gap-6 lg:grid-cols-2">
        <Plate
          title="The affirmation modal"
          note="Overlays float on the larger shadow steps. The confirmation moment is where stet green earns its place."
          importPath="@/components/ui/modal"
        >
          <DialogTrigger>
            <Button color="secondary" size="md">
              Preview the affirmation modal
            </Button>
            <ModalOverlay isDismissable>
              <Modal className="max-w-md">
                <Dialog>
                  {({ close }) => (
                    <div className="bg-primary flex flex-col gap-5 rounded-2xl p-6 shadow-xl">
                      <div className="bg-surface-affirm_tint flex flex-col gap-2 rounded-lg p-4">
                        <span className="bg-surface-affirm text-on-affirm inline-flex w-max items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">
                          Stet · let it stand
                        </span>
                        <p className="text-primary text-md font-semibold">
                          Your suggestion is in
                        </p>
                        <p className="text-secondary text-sm">
                          &ldquo;The Night We Met&rdquo; joins the brief the
                          moment a curator accepts it, and your note travels
                          with it, under your name.
                        </p>
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button color="secondary" size="md" onClick={close}>
                          Keep browsing
                        </Button>
                        <Button color="primary" size="md" onClick={close}>
                          Suggest another
                        </Button>
                      </div>
                    </div>
                  )}
                </Dialog>
              </Modal>
            </ModalOverlay>
          </DialogTrigger>
        </Plate>

        <Plate
          title="The contributor panel"
          note="Slideouts hold the longer read: who is at the desk, and why each track made the cut."
          importPath="@/components/ui/slideout-menu"
        >
          <SlideoutMenu.Trigger>
            <Button color="secondary" size="md">
              Open the contributor panel
            </Button>
            <SlideoutMenu>
              {({ close }) => (
                <>
                  <SlideoutMenu.Header onClose={close}>
                    <div className="flex flex-col gap-0.5 pr-10">
                      <p className="text-primary text-lg font-semibold">
                        At the desk
                      </p>
                      <p className="text-tertiary text-sm">
                        6 contributors · Songs that got me through the first
                        year
                      </p>
                    </div>
                  </SlideoutMenu.Header>
                  <SlideoutMenu.Content>
                    <ul className="flex flex-col gap-5">
                      {WHY_NOTES.map((note) => (
                        <li key={note.id} className="flex gap-3">
                          <Avatar size="sm" name={note.contributor} />
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <p className="text-primary text-sm font-semibold">
                              {note.contributor}
                            </p>
                            <p className="text-secondary text-sm">
                              {note.note}
                            </p>
                            <p className="text-quaternary text-xs">
                              {note.track} — {note.artist}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </SlideoutMenu.Content>
                  <SlideoutMenu.Footer>
                    <div className="flex justify-end">
                      <Button color="primary" size="md" onClick={close}>
                        Add your story
                      </Button>
                    </div>
                  </SlideoutMenu.Footer>
                </>
              )}
            </SlideoutMenu>
          </SlideoutMenu.Trigger>
        </Plate>
      </div>
    </ManualSection>
  )
}
